// 🧾➖ Aplicar Retenciones — Gestión CxC / CxP
// Empareja cada retención con la(s) factura(s) que sustenta (campo
// numDocSustento del XML = serie de la factura) y aplica el valor retenido como
// abono (forma_pago='retencion'), rebajando el saldo de la factura.
//   CxC: retenciones RECIBIDAS → facturas EMITIDAS (mis clientes me retienen)
//   CxP: retenciones EMITIDAS  → facturas RECIBIDAS (yo retengo a proveedores)
// Aplicación individual, masiva ("automático") y reversible.
import { useState } from 'react';
import { CheckCircle2, Download, FileText, Filter, Link2, Receipt, RotateCcw, type LucideIcon } from 'lucide-react';
import { VincularFacturaModal } from './VincularFactura';
import { SortableTh, useSort } from './tablaSort';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const usd = (v: number | string | null) =>
  v == null ? '—' : Number(v).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
// Ruta del archivo en disco → URL servida (igual que en CarteraFacturas/ProcesarDocumentos)
const urlDoc = (p: string | null) => (p ? p.replace(/\/opt\/(?:sri\/documentos|plataforma\/sri-docs)/, '/sri-docs') : null);

interface ModoRet {
  titulo: string;
  descripcion: string;
  contraparteCol: string;        // Agente de retención / Proveedor
  rpcListar: string;
  rpcAplicar: string;
  rpcDesaplicar: string;
  rpcMasivo: string;
  campoRuc: 'agente_ruc' | 'proveedor_ruc';
  campoNombre: 'agente' | 'proveedor';
}

const MODO_CXC: ModoRet = {
  titulo: 'Retenciones Recibidas',
  descripcion: 'Retenciones recibidas aplicadas a las facturas emitidas por cobrar',
  contraparteCol: 'Agente de retención',
  rpcListar: 'cxc_ret_listar', rpcAplicar: 'cxc_ret_aplicar',
  rpcDesaplicar: 'cxc_ret_desaplicar', rpcMasivo: 'cxc_ret_aplicar_masivo',
  campoRuc: 'agente_ruc', campoNombre: 'agente',
};
const MODO_CXP: ModoRet = {
  titulo: 'Retenciones Emitidas',
  descripcion: 'Retenciones emitidas aplicadas a las facturas recibidas por pagar',
  contraparteCol: 'Proveedor',
  rpcListar: 'cxp_ret_listar', rpcAplicar: 'cxp_ret_aplicar',
  rpcDesaplicar: 'cxp_ret_desaplicar', rpcMasivo: 'cxp_ret_aplicar_masivo',
  campoRuc: 'proveedor_ruc', campoNombre: 'proveedor',
};

export function CxcRetenciones() { return <AplicarRetenciones cfg={MODO_CXC} icono={Receipt} />; }
export function CxpRetenciones() { return <AplicarRetenciones cfg={MODO_CXP} icono={Receipt} />; }

interface RetencionFila {
  ret_id: string;
  ret_serie: string;
  fecha_emision: string | null;
  num_doc_sustento: string | null;
  monto: number;
  factura_serie: string | null;
  factura_importe: number | null;
  xml_url: string | null;
  ride_url: string | null;
  estado: 'aplicada' | 'pendiente' | 'sin_factura';
  [k: string]: unknown;
}

const ESTADO_CHIP: Record<string, string> = {
  aplicada: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  pendiente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  sin_factura: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};
const ESTADO_LABEL: Record<string, string> = { aplicada: 'Aplicada', pendiente: 'Pendiente', sin_factura: 'Sin factura' };
const selectCls = 'h-9 rounded-md border border-input bg-background px-2 text-sm';

const ESTADOS_KPI = [
  { k: 'pendiente', label: 'Pendientes', cls: 'text-yellow-700 dark:text-yellow-300' },
  { k: 'aplicada', label: 'Aplicadas', cls: 'text-green-700 dark:text-green-300' },
  { k: 'sin_factura', label: 'Sin factura destino', cls: 'text-red-700 dark:text-red-300' },
] as const;

function AplicarRetenciones({ cfg, icono: Icono }: { cfg: ModoRet; icono: LucideIcon }) {
  const queryClient = useQueryClient();
  const anioActual = new Date().getFullYear();

  const [filtros, setFiltros] = useState({ estado: 'pendientes', anio: '', mes: '', serie: '' });
  const [aplicados, setAplicados] = useState(filtros);
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());

  const { data: filas = [], isFetching } = useQuery<RetencionFila[]>({
    queryKey: ['retListar', cfg.rpcListar, aplicados],
    queryFn: async () => {
      const { data } = await apiClient.post(`/rpc/${cfg.rpcListar}`, {
        _estado: aplicados.estado,
        _anio: aplicados.anio ? Number(aplicados.anio) : null,
        _mes: aplicados.mes ? Number(aplicados.mes) : null,
        _serie: aplicados.serie || null,
      }, FIN);
      return data || [];
    },
  });

  const refrescar = () => {
    void queryClient.invalidateQueries({ queryKey: ['retListar'] });
    void queryClient.invalidateQueries({ queryKey: ['cartera'] });
    setSeleccion(new Set());
  };

  // Desglose por estado (todos los estados del período, sin importar el filtro de estado)
  const { data: filasKpi = [] } = useQuery<RetencionFila[]>({
    queryKey: ['retKpi', cfg.rpcListar, aplicados.anio, aplicados.mes, aplicados.serie],
    queryFn: async () => {
      const { data } = await apiClient.post(`/rpc/${cfg.rpcListar}`, {
        _estado: 'todas',
        _anio: aplicados.anio ? Number(aplicados.anio) : null,
        _mes: aplicados.mes ? Number(aplicados.mes) : null,
        _serie: aplicados.serie || null,
      }, FIN);
      return data || [];
    },
  });
  const kpis = ESTADOS_KPI.map((e) => {
    const items = filasKpi.filter((f) => f.estado === e.k);
    return {
      ...e,
      n: new Set(items.map((f) => f.ret_id)).size, // retenciones únicas (no líneas/sustentos)
      monto: items.reduce((s, f) => s + Number(f.monto || 0), 0),
    };
  });

  // IDs únicos de retenciones aplicables (pueden repetirse por multi-factura)
  const esCxc = cfg.rpcListar === 'cxc_ret_listar';
  const [vinculando, setVinculando] = useState<RetencionFila | null>(null);
  const vincular = useMutation({
    mutationFn: async (ticketId: string) =>
      (await apiClient.post(`/rpc/${esCxc ? 'cxc' : 'cxp'}_ret_vincular_factura`,
        { _ret_id: vinculando!.ret_id, _num_doc_actual: vinculando!.num_doc_sustento, _ticket_id: ticketId }, FIN)).data,
    onSuccess: () => { toast.success('Factura vinculada'); setVinculando(null); refrescar(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo vincular'),
  });

  const retSort = useSort<RetencionFila>(filas);
  const aplicablesIds = [...new Set(filas.filter((f) => f.estado === 'pendiente').map((f) => f.ret_id))];
  const todosSel = aplicablesIds.length > 0 && aplicablesIds.every((id) => seleccion.has(id));

  // Exportar (CSV / XLS / PDF) — respeta filtros y orden actuales
  const nombreArchivo = `retenciones-${esCxc ? 'cxc' : 'cxp'}-${fechaLocal()}`;
  const headersExp = ['N°', 'Fecha', 'Retención', cfg.contraparteCol, 'Factura sustentada', 'Retenido', 'Estado'];
  const filasExport: Celda[][] = [
    ...retSort.sorted.map((f, i): Celda[] => [
      i + 1, f.fecha_emision ?? '', f.ret_serie ?? '', String(f[cfg.campoNombre] ?? ''), f.num_doc_sustento ?? '', Number(f.monto || 0), f.estado ?? '',
    ]),
    ['', '', '', '', 'TOTAL', retSort.sorted.reduce((a, f) => a + Number(f.monto || 0), 0), ''],
  ];

  const aplicarUna = useMutation({
    mutationFn: async (retId: string) => (await apiClient.post(`/rpc/${cfg.rpcAplicar}`, { _ret_id: retId }, FIN)).data,
    onSuccess: (r: any) => { toast.success(r?.message || 'Aplicada'); refrescar(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo aplicar'),
  });
  const desaplicar = useMutation({
    mutationFn: async (retId: string) => (await apiClient.post(`/rpc/${cfg.rpcDesaplicar}`, { _ret_id: retId }, FIN)).data,
    onSuccess: () => { toast.success('Aplicación revertida'); refrescar(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo revertir'),
  });
  const masivo = useMutation({
    mutationFn: async () => (await apiClient.post(`/rpc/${cfg.rpcMasivo}`, { _ret_ids: [...seleccion] }, FIN)).data,
    onSuccess: (r: any) => {
      if (r.errores?.length) toast.warning(`${r.aplicadas} aplicadas · ${r.errores.length} con error (${r.errores[0].error})`);
      else toast.success(`${r.aplicadas} retenciones aplicadas`);
      refrescar();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo aplicar el lote'),
  });

  const toggle = (id: string) => setSeleccion((p) => { const s = new Set(p); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const dlg = useDialogoEnLinea();

  return (
    <div className="p-6 space-y-6">
      {dlg.ui}
      <div className="flex items-center gap-3">
        <Icono className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">{cfg.titulo}</h1>
          <p className="text-sm text-muted-foreground">{cfg.descripcion}</p>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="rt-estado">Estado</label>
              <select id="rt-estado" className={selectCls} value={filtros.estado}
                onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}>
                <option value="todas">Todas</option>
                <option value="pendientes">Pendientes (con factura)</option>
                <option value="aplicadas">Aplicadas</option>
                <option value="sin_factura">Sin factura</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="rt-anio">Año</label>
              <select id="rt-anio" className={selectCls} value={filtros.anio}
                onChange={(e) => setFiltros({ ...filtros, anio: e.target.value })}>
                <option value="">Todos</option>
                {Array.from({ length: anioActual - 2014 }, (_, i) => anioActual - i).map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="rt-mes">Mes</label>
              <select id="rt-mes" className={selectCls} value={filtros.mes}
                onChange={(e) => setFiltros({ ...filtros, mes: e.target.value })}>
                <option value="">Todos</option>
                {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="rt-serie">Serie</label>
              <input id="rt-serie" className={selectCls} placeholder="Retención o factura…"
                value={filtros.serie}
                onChange={(e) => setFiltros({ ...filtros, serie: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { setAplicados(filtros); setSeleccion(new Set()); } }} />
            </div>
            <Button size="sm" onClick={() => { setAplicados(filtros); setSeleccion(new Set()); }} isLoading={isFetching}>
              {!isFetching && <Filter className="w-4 h-4" />} Filtrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs por estado (monto $) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {kpis.map((e) => (
          <Card key={e.k}>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground">{e.label}</p>
              <p className={`text-2xl font-bold ${e.cls}`}>{usd(e.monto)}</p>
              <p className="text-xs text-muted-foreground">{e.n} retención{e.n === 1 ? '' : 'es'}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Barra masiva (automático) */}
      {seleccion.size > 0 && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <p className="text-sm font-semibold">{seleccion.size} retención(es) seleccionada(s)</p>
            <Button size="sm" isLoading={masivo.isPending} onClick={() => masivo.mutate()}>
              {!masivo.isPending && <CheckCircle2 className="w-4 h-4" />} Aplicar {seleccion.size}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSeleccion(new Set())}>Limpiar</Button>
          </CardContent>
        </Card>
      )}

      {/* Tabla */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">Retenciones ({filas.length})</CardTitle>
            <ExportarBotones
              nombre={nombreArchivo} titulo={cfg.titulo} headers={headersExp} filas={filasExport}
              colsDerecha={[0, 5]} subtitulo={`${new Date().toLocaleString(LOCALE)} · ${retSort.sorted.length} línea(s)`}
              fmtPDF={(v, c) => (c === 5 ? usd(Number(v)) : String(v ?? ''))}
              disabled={retSort.sorted.length === 0} />
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right w-12">N°</th>
                <th className="py-2 pr-2 w-8">
                  <input type="checkbox" className="h-4 w-4 rounded border-input align-middle"
                    checked={todosSel}
                    onChange={() => setSeleccion(todosSel ? new Set() : new Set(aplicablesIds))}
                    title="Seleccionar todas las aplicables" />
                </th>
                <SortableTh col="fecha_emision" label="Fecha" sort={retSort.sort} onSort={retSort.toggle} />
                <SortableTh col="ret_serie" label="Retención" sort={retSort.sort} onSort={retSort.toggle} />
                <SortableTh col={cfg.campoNombre} label={cfg.contraparteCol} sort={retSort.sort} onSort={retSort.toggle} />
                <SortableTh col="num_doc_sustento" label="Factura sustentada" sort={retSort.sort} onSort={retSort.toggle} />
                <SortableTh col="monto" label="Retenido" align="right" sort={retSort.sort} onSort={retSort.toggle} />
                <th className="py-2 pr-3">Comprobante</th>
                <SortableTh col="estado" label="Estado" sort={retSort.sort} onSort={retSort.toggle} />
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {retSort.sorted.length === 0 && (
                <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">
                  Sin retenciones para los filtros seleccionados
                </td></tr>
              )}
              {retSort.sorted.map((f, idx) => (
                <tr key={`${f.ret_id}-${f.num_doc_sustento}-${idx}`} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">{idx + 1}</td>
                  <td className="py-2 pr-2">
                    <input type="checkbox" className="h-4 w-4 rounded border-input align-middle"
                      checked={seleccion.has(f.ret_id)}
                      disabled={f.estado !== 'pendiente'}
                      onChange={() => toggle(f.ret_id)} />
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">{f.fecha_emision || '—'}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{f.ret_serie}</td>
                  <td className="py-2 pr-3">
                    <p className="truncate max-w-52" title={String(f[cfg.campoNombre] || '')}>{String(f[cfg.campoNombre] || '—')}</p>
                    <p className="text-xs text-muted-foreground">{String(f[cfg.campoRuc] || '')}</p>
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {f.factura_serie || f.num_doc_sustento || '—'}
                    {f.factura_importe != null && <span className="text-xs text-muted-foreground"> ({usd(f.factura_importe)})</span>}
                  </td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(f.monto)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {urlDoc(f.xml_url) && (
                      <a href={urlDoc(f.xml_url)!} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline" title="Ver XML">
                        <Download className="w-4 h-4" /> XML
                      </a>
                    )}
                    {urlDoc(f.ride_url) && (
                      <a href={urlDoc(f.ride_url)!} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline" title="Ver RIDE">
                        <FileText className="w-4 h-4" /> RIDE
                      </a>
                    )}
                    {!urlDoc(f.xml_url) && !urlDoc(f.ride_url) && <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-2 pr-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${ESTADO_CHIP[f.estado]}`}
                      title={f.estado === 'sin_factura' ? 'La factura no está en cartera (descárguela y procésela)' : ''}>
                      {ESTADO_LABEL[f.estado]}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    {f.estado === 'pendiente' && (
                      <Button size="sm" isLoading={aplicarUna.isPending} onClick={() => aplicarUna.mutate(f.ret_id)}>Aplicar</Button>
                    )}
                    {f.estado === 'aplicada' && (
                      <Button size="sm" variant="outline" isLoading={desaplicar.isPending}
                        onClick={async () => {
                          if (await dlg.confirmar(`¿Revertir la aplicación de la retención ${f.ret_serie}?`, { detalle: 'El saldo de la factura volverá a subir.' })) {
                            desaplicar.mutate(f.ret_id);
                          }
                        }}>
                        <RotateCcw className="w-3.5 h-3.5" /> Revertir
                      </Button>
                    )}
                    {f.estado === 'sin_factura' && (
                      <Button size="sm" variant="outline" onClick={() => setVinculando(f)}>
                        <Link2 className="w-3.5 h-3.5" /> Vincular factura
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {retSort.sorted.length > 0 && (
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3 text-right" colSpan={6}>Totales ({retSort.sorted.length})</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(retSort.sorted.reduce((a, f) => a + Number(f.monto || 0), 0))}</td>
                  <td colSpan={3}></td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {vinculando && (
        <VincularFacturaModal
          facturasRpc={esCxc ? 'cxc_listar' : 'cxp_listar'}
          contraparte={cfg.contraparteCol}
          detalle={`Retención ${vinculando.ret_serie} · sustenta ${vinculando.num_doc_sustento ?? '—'} · ${usd(vinculando.monto)} — elige la factura correcta.`}
          vinculando={vincular.isPending}
          onClose={() => setVinculando(null)}
          onPick={(ticketId) => vincular.mutate(ticketId)}
        />
      )}
    </div>
  );
}
